import React, { createContext, useContext, useState } from "react";

// Create the language context
const LanguageContext = createContext();

// Define the translations
export const translations = {
	en: {
		// General
		back: "Back to Education",
		
		 // Education Table Headers
		educationTableHeaders: {
			projectName: "Project Name",
			realHours: "Real Hours",
			validatedHours: "Validated Hours",
			theme: "Theme",
			details: "Details"
		},
		
		// Navbar
		home: "Home",
		about: "About",
		projects: "Projects",
		experience: "Experience",
		education: "Education",
		contact: "Contact",
		careerPlan: "Career Plan",
		portfolio: "My Portfolio",
		

		// Hero section
		greeting: "Hello, I'm",
		role: "Full Stack Developer",
		heroDesc:
			"Passionate about creating responsive web applications with modern technologies. I focus on building clean, efficient, and user-friendly solutions.",
		downloadResume: "Download Resume",
		contactMe: "Contact Me",

		// About section
		aboutTitle: "About Me",
		aboutSubtitle: "Get to know me better",
		aboutWhoAmI: "Who I Am",
		aboutParagraph1:
			"I'm a passionate Full Stack Developer with a strong focus on web technologies and a growing expertise in cybersecurity. With a background in both front-end and back-end development, I strive to create seamless, user-friendly applications that solve real-world problems.",
		aboutParagraph2:
			"My strengths include problem-solving, rapid adaptation to new technologies, and effective communication with both technical and non-technical stakeholders. I thrive in collaborative environments while also being self-disciplined enough to work independently.",
		aboutParagraph3:
			"Beyond coding, I'm actively pursuing knowledge in cybersecurity through formal education and hands-on practice in CTF competitions, with the goal of combining development and security expertise.",
		aboutSkills: "Technical Skills",

		// Projects section
		projectsTitle: "Projects",
		projectsSubtitle: "Check out some of my recent work",
		viewProject: "View Project",
		viewCode: "View Code",
		technologies: "Technologies",

		// Career Plan
		careerPlanTitle: "Career Plan",
		careerPlanSubtitle: "My journey in cybersecurity and entrepreneurship",
		shortTerm: "Short Term Goals",
		shortTermGoals: [
			"Master React and Node.js ecosystems",
			"Contribute to open-source projects",
			"Improve UI/UX design skills",
		],
		midTerm: "Mid Term Goals",
		midTermGoals: [
			"Lead development of medium to large scale projects",
			"Specialize in cloud architecture",
			"Mentor junior developers",
		],
		longTerm: "Long Term Goals",
		longTermGoals: [
			"Become a technical architect",
			"Start a tech consultancy business",
			"Speak at international tech conferences",
		],
		cyberSecurityTraining: "Cybersecurity Training",
		cyberSecurityTrainingGoals: [
			"Pursuing a cybersecurity master's degree",
			"Participating in CTF competitions for 2-3 years",
			"Completing specialized tutorials and training courses",
			"Building practical skills through hands-on projects",
		],
		pentestCareer: "Penetration Testing Career",
		pentestCareerGoals: [
			"Becoming a certified penetration tester",
			"Mastering security testing methodologies",
			"Specializing in vulnerability assessment",
			"Contributing to the security community",
		],
		entrepreneurship: "Entrepreneurial Venture",
		entrepreneurshipGoals: [
			"Co-founding a tech startup with Samuel Lambert",
			"Developing a mobile application for inventory management",
			"Building a scalable business model",
			"Bringing innovative solutions to market",
		],

		// Experience
		experienceTitle: "Work Experience",
		experienceSubtitle: "My professional journey",
		present: "Present",
		roles: {
			role1: "Internship at Axa",
			role2: "CEO",
			role3: "Field Technician",
			role4: "Vendor & Cashier",
			role5: "Desk Vendor",
			role6: "File Manager",
			role7: "Beginner Animator",
		},
		companies: {
			company1: "AXA BELGIUM",
			company2: "SAMJU INC.",
			company3: "MISTERGENIUS",
			company4: "SPAR",
			company5: "SUSHI SHOP",
			company6: "AXA BELGIUM",
			company7: "ASBL ECOLE DU MOUVEMENT",
		},
		descriptions: {
			desc1: "Internship at Axa, working on several projects for the company",
			desc2: "Creating our company and develop our mobile application",
			desc3: "Helping people with technology",
			desc4: "Vendor and cashier in a supermarket",
			desc5: "Selling sushi and dispatching deliveries",
			desc6: "Working for axa clients and helping them with their files during Belgium 2021 floods",
			desc7: "Creating parcours for children and animating them every sunday's morning during 2 years",
		},

		// Education Projects
		educationProjects: {
			technicalSupport: {
				title: "Technical Support at MisterGenius",
				skills: "Student Job",
				careerBenefit: "Network building, Interpersonal communication skills",
				description: "Worked as a field technician helping people repair their laptops and answering technology questions."
			},
			picoCTF: {
				title: "PicoCTF 2023",
				skills: "Capture The Flag",
				careerBenefit: "Enhanced security knowledge and analytical thinking",
				description: "Participated in PicoCTF 2023, a cybersecurity competition focused on solving various security challenges and capture the flag exercises."
			},
			stock4me: {
				title: "Stock4me - SAMJU Inc.",
				skills: "Entrepreneurship",
				careerBenefit: "Business development, Startup experience, Project management",
				description: "Co-founded SAMJU Inc. and developed Stock4me, an AI-powered mobile application for inventory management."
			},
			cyberConference: {
				title: "Cybersecurity: A Real Virtual Battle",
				skills: "Conference",
				careerBenefit: "Industry insights, Professional networking, Security awareness",
				description: "Attended a cybersecurity conference presented by Vincent Ceriani and Benoit Debande at Ephec Louvain-La-Neuve, focusing on real-world cyber threats and defense strategies."
			},
			odooVisit: {
				title: "Odoo Office Visit",
				skills: "Office Visit",
				careerBenefit: "Industry exposure, Professional environment understanding, Software development practices",
				description: "Visited Odoo's office in Louvain-La-Neuve, learning about their work environment, development practices, and participating in hands-on exercises during an interactive afternoon session."
			},
			ethicalHacking: {
				title: "Ethical Hacking: The Complete Course",
				skills: "E-Learning",
				careerBenefit: "Advanced security knowledge, Practical hacking skills, Professional certification",
				description: "Completed a comprehensive ethical hacking course on Udemy by Michel Kartner and Cyberini Formation, covering penetration testing, security analysis, and ethical hacking methodologies."
			},
			sushiShop: {
				title: "Desk Vendor at Sushi Shop",
				skills: "Soft Skills",
				careerBenefit: "Interpersonal skills, Team management, Problem-solving in fast-paced environment",
				description: "Worked as a desk vendor at Sushi Shop from August 2022 to January 2023, managing customer orders, coordinating with delivery personnel, and ensuring efficient service delivery."
			},
			axaHackathon: {
				title: "AXA IT Job Days & Hackathon",
				skills: "Hackathon",
				careerBenefit: "Industry networking, Real-world problem solving, Corporate environment exposure",
				description: "Participated in AXA's IT Job Days and Hackathon, working on innovative solutions in a corporate environment while networking with industry professionals."
			},
			spar: {
				title: "Vendor & Cashier at Spar Mont-Saint-Guibert",
				skills: "Customer Service, Cash Handling, Team Coordination, Inventory Management",
				careerBenefit: "Problem-solving, Interpersonal skills, Time management, Multi-tasking abilities",
				description: "Worked as a vendor and cashier at Spar Mont-Saint-Guibert from September 2023 to December 2023, managing customer transactions, store inventory, and providing excellent customer service."
				},
			axaStudentJob: {
				title: "AXA Belgium - File Management During 2021 Floods",
				skills: "Student Job",
				careerBenefit: "Enhanced crisis management skills and process optimization in an insurance environment during natural disaster",
				description: "Managed client files during Belgium floods crisis, implementing efficiency improvements and supporting team operations"
			},
		},

			// Project Pages
		projectPages: {
			proofOfParticipation: "Proof of Participation",
			visitProject: "Visit Project",
			visitWebsite: "Visit Website",
			viewDocumentation: 'View Documentation',
viewGithub: 'View GitHub',
			backToEducation: "Back to Education",
			projectOverview: "Project Overview",
			skillsDeveloped: "Skills Developed",
			careerBenefits: "Career Benefits",
			projectDetails: "Project Details",
			responsibilities: "Responsibilities",
			challengesSolutions: "Challenges & Solutions",
			challenge: "Challenge",
			solution: "Solution",
			keyOutcomes: "Key Outcomes",
			careerPlanConnection: "Career Plan Connection",
			
			technicalSupport: {
				title: "Technical Support at MisterGenius",
				subtitle: "10-hour project focused on developing technical and communication skills",
				overview: "As a field technician at MisterGenius, I provided on-site technical support to customers with various hardware and software issues. This hands-on experience allowed me to develop practical troubleshooting skills while enhancing my client communication abilities.",
				skills: [
					"Hardware diagnostics and repair",
					"Software troubleshooting",
					"Customer service excellence",
					"Technical communication",
					"Problem-solving under pressure",
					"Time management and efficiency"
				],
				careerBenefits: [
					{
						title: "Professional Network",
						description: "Built relationships with clients and industry professionals"
					},
					{
						title: "Communication Skills",
						description: "Enhanced ability to explain technical concepts to non-technical users"
					},
					{
						title: "Adaptability",
						description: "Learned to work in diverse environments and with various client needs"
					},
					{
						title: "Technical Expertise",
						description: "Gained hands-on experience with a wide range of hardware and software issues"
					}
				],
				responsibilities: [
					"Diagnosing and resolving hardware issues on various laptop models",
					"Troubleshooting software problems including operating system errors, malware removal, and application conflicts",
					"Providing clear explanations and recommendations to clients",
					"Documenting service calls and solutions implemented",
					"Following up with clients to ensure satisfaction with services provided"
				],
				challenges: [
					{
						challenge: "Explaining complex technical issues to non-technical clients",
						solution: "Developed analogies and simple explanations to make technical concepts accessible"
					},
					{
						challenge: "Diagnosing intermittent hardware failures",
						solution: "Created a systematic testing approach to identify and isolate problematic components"
					}
				],
				outcomes: [
					"Received positive feedback for clear communication and effective solutions",
					"Built a network of repeat clients and referrals",
					"Gained practical experience in laptop repair and troubleshooting"
				],
				careerConnections: [
					{
						title: "Technical Foundation",
						description: "The hands-on troubleshooting experience provides a strong foundation for understanding hardware and software interactions, which is essential for my career in IT. This practical knowledge complements my academic learning and gives me a real-world perspective on technology systems."
					},
					{
						title: "Communication Skills",
						description: "The ability to effectively communicate technical concepts to non-technical users is a crucial skill in IT roles. This experience has significantly improved my ability to bridge the gap between technical expertise and clear client communication, a skill that will be valuable throughout my career."
					},
					{
						title: "Professional Network",
						description: "Building a network of satisfied clients and industry connections provides valuable professional relationships that can lead to future opportunities. These connections may offer mentorship, job referrals, or collaborative projects as I progress in my career."
					}
				]
			},
			picoCTF: {
				title: "PicoCTF 2023 - Cybersecurity Competition",
				subtitle: "A challenging capture-the-flag competition focused on developing practical cybersecurity skills",
				overview: "Participated in PicoCTF 2023, a premier cybersecurity competition designed by security experts at Carnegie Mellon University. This competition provided hands-on experience in various aspects of cybersecurity, from cryptography to binary exploitation.",
				skills: [
					"Cryptographic analysis",
					"Binary exploitation",
					"Web vulnerability assessment",
					"Reverse engineering",
					"Forensics investigation",
					"Problem-solving under pressure",
					"Security tool proficiency"
				],
				careerBenefits: [
					{
						title: "Technical Expertise",
						description: "Developed practical skills in various cybersecurity domains"
					},
					{
						title: "Problem-Solving",
						description: "Enhanced analytical thinking and debugging capabilities"
					},
					{
						title: "Security Mindset",
						description: "Strengthened understanding of security vulnerabilities and attack vectors"
					},
					{
						title: "Industry Recognition",
						description: "Gained experience with industry-standard security tools and methodologies"
					}
				],
				responsibilities: [
					"Analyzing and solving complex security challenges",
					"Implementing various cryptographic techniques",
					"Performing reverse engineering on binary files",
					"Identifying and exploiting web vulnerabilities",
					"Conducting forensic analysis on different data formats",
					"Documenting solutions and methodologies"
				],
				challenges: [
					{
						challenge: "Complex cryptographic puzzles requiring deep understanding",
						solution: "Developed systematic approach to analyze and break various encryption methods"
					},
					{
						challenge: "Time-sensitive challenges requiring quick thinking",
						solution: "Created a methodology to quickly assess and prioritize challenges based on complexity"
					},
					{
						challenge: "Advanced binary exploitation problems",
						solution: "Built a comprehensive toolkit for analyzing and manipulating binary files"
					}
				],
				outcomes: [
					"Successfully solved multiple challenges across different security domains",
					"Developed a comprehensive understanding of various security concepts",
					"Created a personal toolkit of scripts and methodologies for future challenges",
					"Improved ability to think like both an attacker and defender"
				],
				careerConnections: [
					{
						title: "Practical Security Skills",
						description: "The hands-on experience gained through PicoCTF directly aligns with my goal of becoming a security professional. The challenges mirror real-world scenarios, providing valuable experience in identifying and exploiting vulnerabilities."
					},
					{
						title: "Technical Growth",
						description: "Each challenge required learning new tools and techniques, contributing to my technical expertise in security. This continuous learning approach is essential for staying current in the cybersecurity field."
					},
					{
						title: "Problem-Solving Development",
						description: "The competition's diverse challenges enhanced my analytical and problem-solving abilities, crucial skills for both cybersecurity and software development roles."
					}
				]
			},
			stock4me: {
				title: "Stock4me - SAMJU Inc.",
				subtitle: "Co-founding a startup and developing an AI-powered inventory management solution",
				overview: "Co-founded SAMJU Inc. and led the development of Stock4me, an innovative mobile application that leverages artificial intelligence to streamline inventory management processes. This entrepreneurial venture combines technical expertise with business acumen.",
				skills: [
					"Mobile application development",
					"AI/ML integration",
					"Business development",
					"Project management",
					"UI/UX design",
					"Database architecture",
					"Team leadership"
				],
				careerBenefits: [
					{
						title: "Entrepreneurial Experience",
						description: "Gained firsthand experience in starting and running a technology startup"
					},
					{
						title: "Technical Leadership",
						description: "Led development decisions and technical architecture planning"
					},
					{
						title: "Business Acumen",
						description: "Developed skills in business planning, market analysis, and product strategy"
					},
					{
						title: "Project Management",
						description: "Managed development timeline, resources, and stakeholder expectations"
					}
				],
				responsibilities: [
					"Leading product vision and development strategy",
					"Designing and implementing core application features",
					"Integrating AI capabilities for inventory prediction",
					"Managing development team and project timeline",
					"Conducting market research and competitor analysis",
					"Creating business plans and presentation materials",
					"Engaging with potential clients and investors"
				],
				challenges: [
					{
						challenge: "Balancing technical development with business needs",
						solution: "Implemented agile methodology to prioritize features based on market feedback and business value"
					},
					{
						challenge: "Integrating complex AI capabilities into a user-friendly interface",
						solution: "Developed modular architecture and intuitive UI patterns to simplify user interaction with AI features"
					},
					{
						challenge: "Managing resource constraints in a startup environment",
						solution: "Created efficient development processes and leveraged open-source tools to maximize productivity"
					}
				],
				outcomes: [
					"Successfully launched beta version of Stock4me application",
					"Developed robust AI-powered inventory management features",
					"Built strong foundation for business growth and scalability",
					"Established network of potential clients and partners",
					"Created efficient development and business processes"
				],
				careerConnections: [
					{
						title: "Technology Innovation",
						description: "Developing Stock4me has provided invaluable experience in combining cutting-edge technology with practical business solutions. This aligns perfectly with my goal of creating innovative solutions that solve real-world problems."
					},
					{
						title: "Leadership Development",
						description: "Managing both technical and business aspects of the startup has strengthened my leadership capabilities, preparing me for future roles in technology leadership and entrepreneurship."
					},
					{
						title: "Business & Technology Integration",
						description: "This experience bridges the gap between technical expertise and business acumen, essential for my long-term goal of running successful technology ventures."
					}
				]
			},
			cyberConference: {
				title: "Cybersecurity: A Real Virtual Battle",
				subtitle: "An insightful conference on modern cybersecurity challenges and defense strategies",
				overview: "Attended an engaging cybersecurity conference presented by industry experts Vincent Ceriani and Benoit Debande at Ephec Louvain-La-Neuve. The conference provided valuable insights into current cyber threats, defense mechanisms, and the evolving landscape of information security.",
				skills: [
					"Security threat assessment",
					"Defense strategy understanding",
					"Network security concepts",
					"Risk analysis",
					"Security best practices",
					"Industry trend awareness",
					"Professional networking"
				],
				careerBenefits: [
					{
						title: "Industry Insights",
						description: "Gained valuable perspectives on real-world cybersecurity challenges and solutions"
					},
					{
						title: "Professional Connections",
						description: "Networked with industry experts and fellow cybersecurity enthusiasts"
					},
					{
						title: "Knowledge Enhancement",
						description: "Deepened understanding of current security threats and defense strategies"
					},
					{
						title: "Career Direction",
						description: "Reinforced career path in cybersecurity through expert guidance"
					}
				],
				responsibilities: [
					"Active participation in conference discussions",
					"Taking detailed notes on presented security concepts",
					"Engaging with speakers during Q&A sessions",
					"Analyzing presented case studies and examples",
					"Networking with industry professionals",
					"Reflecting on learned concepts for practical application"
				],
				challenges: [
					{
						challenge: "Understanding complex technical concepts presented rapidly",
						solution: "Developed efficient note-taking system and reviewed materials post-conference"
					},
					{
						challenge: "Relating theoretical concepts to practical applications",
						solution: "Created personal examples and scenarios to contextualize the information"
					},
					{
						challenge: "Maximizing networking opportunities in limited time",
						solution: "Prepared relevant questions beforehand and actively engaged during breaks"
					}
				],
				outcomes: [
					"Enhanced understanding of current cybersecurity landscape",
					"Established connections with industry professionals",
					"Gained insights into real-world security challenges",
					"Collected valuable resources for further learning",
					"Identified key areas for skill development"
				],
				careerConnections: [
					{
						title: "Professional Development",
						description: "The conference provided crucial insights into the cybersecurity industry, helping shape my understanding of career requirements and opportunities. The exposure to real-world challenges and solutions has informed my professional development path."
					},
					{
						title: "Network Building",
						description: "Meeting and learning from experienced professionals has expanded my professional network and provided valuable mentorship opportunities. These connections are invaluable for future career growth in cybersecurity."
					},
					{
						title: "Knowledge Application",
						description: "The practical insights gained from the conference directly support my goals in cybersecurity, providing real-world context to my technical learning and helping focus my skill development efforts."
					}
				]
			},
			spar: {
				title: "Vendor & Cashier at Spar Mont-Saint-Guibert",
				subtitle: "A comprehensive retail experience developing essential soft skills and operational expertise",
				overview: "From September 2023 to December 2023, I worked as both a vendor and cashier at Spar Mont-Saint-Guibert, where I developed essential customer service skills and gained valuable operational experience in retail management.",
				skills: [
					"Customer Service Excellence",
					"Cash Management",
					"Inventory Control",
					"Team Collaboration",
					"Problem Resolution",
					"Time Management"
				],
				careerBenefits: [
					{
						title: "Multi-tasking",
						description: "Managing multiple responsibilities"
					},
					{
						title: "Communication",
						description: "Clear customer interaction"
					},
					{
						title: "Problem-Solving",
						description: "Quick resolution of issues"
					},
					{
						title: "Teamwork",
						description: "Effective collaboration"
					}
				],
				responsibilities: {
					customerService: {
						title: "Customer Service",
						description: "Providing excellent customer service and handling inquiries professionally"
					},
					cashHandling: {
						title: "Cash Handling",
						description: "Managing transactions accurately and maintaining cash register balance"
					},
					inventoryManagement: {
						title: "Inventory Management",
						description: "Monitoring stock levels and assisting with inventory control"
					},
					storeOperations: {
						title: "Store Operations",
						description: "Maintaining store cleanliness and product organization"
					}
				},
				achievements: [
					"Consistently provided excellent customer service",
					"Maintained accurate cash handling records",
					"Contributed to efficient store operations",
					"Developed strong product knowledge",
					"Built positive relationships with regular customers"
				],
				careerConnections: [
					{
						title: "Soft Skills Development",
						description: "The retail environment provided excellent opportunities to develop crucial soft skills such as communication, problem-solving, and teamwork. These skills are valuable in any professional setting, including technology roles."
					},
					{
						title: "Professional Growth",
						description: "Working in a fast-paced retail environment helped develop adaptability and multi-tasking abilities. These skills are particularly valuable when managing multiple projects and deadlines in a technical career."
					}
				],
				backButton: "Back to Education"
			},
			odooVisit: {
				title: "Odoo Office Visit",
				subtitle: "An insightful exploration of professional software development practices",
				overview: "Participated in a comprehensive visit to Odoo's office in Louvain-La-Neuve, gaining valuable insights into their work environment, development practices, and software development lifecycle through interactive sessions and hands-on exercises.",
				skills: [
					"Software Development Practices",
					"ERP Systems Understanding",
					"Professional Environment Awareness",
					"Development Workflow Knowledge",
					"Team Collaboration Methods",
					"Industry Best Practices",
					"Technical Communication"
				],
				careerBenefits: [
					{
						title: "Industry Exposure",
						description: "Direct insight into professional software development environment"
					},
					{
						title: "Professional Network",
						description: "Connected with industry professionals and developers"
					},
					{
						title: "Technical Knowledge",
						description: "Understanding of enterprise-level software development"
					},
					{
						title: "Career Guidance",
						description: "Valuable perspective on professional development paths"
					}
				],
				responsibilities: [
					"Participating in interactive sessions about development practices",
					"Engaging with Odoo developers and team members",
					"Learning about ERP systems architecture",
					"Understanding agile development processes",
					"Exploring modern development tools and methodologies",
					"Analyzing real-world software development scenarios"
				],
				challenges: [
					{
						challenge: "Understanding complex enterprise software architecture",
						solution: "Actively engaged with developers and took detailed notes for later review"
					},
					{
						challenge: "Absorbing large amount of technical information",
						solution: "Created structured documentation and asked clarifying questions"
					},
					{
						challenge: "Relating theoretical knowledge to practical applications",
						solution: "Participated in hands-on exercises and real-world examples"
					}
				],
				outcomes: [
					"Gained practical insight into professional software development",
					"Understood enterprise-level development practices",
					"Learned about modern development tools and methodologies",
					"Built connections with industry professionals",
					"Enhanced understanding of career opportunities in software development"
				],
				careerConnections: [
					{
						title: "Professional Development",
						description: "The visit provided valuable insight into professional software development practices and workplace culture, helping inform my career development path and professional aspirations."
					},
					{
						title: "Technical Growth",
						description: "Understanding enterprise-level software development and architecture has broadened my technical perspective and highlighted areas for future learning and development."
					},
					{
						title: "Industry Understanding",
						description: "Direct exposure to a professional software development environment has helped bridge the gap between academic learning and industry practice, providing valuable context for my career development."
					}
				]
			},
			ethicalHacking: {
				title: "Ethical Hacking: The Complete Course",
				subtitle: "A comprehensive journey into ethical hacking and cybersecurity fundamentals",
				overview: "Completed an extensive ethical hacking course on Udemy by Michel Kartner and Cyberini Formation, gaining in-depth knowledge of penetration testing, security analysis, and ethical hacking methodologies through hands-on practice and real-world scenarios.",
				skills: [
					"Penetration Testing Methodologies",
					"Network Security Analysis",
					"Vulnerability Assessment",
					"Security Tool Proficiency",
					"Social Engineering Techniques",
					"Web Application Security",
					"System Exploitation",
					"Incident Response",
					"Security Report Writing"
				],
				careerBenefits: [
					{
						title: "Professional Certification",
						description: "Gained industry-recognized certification in ethical hacking"
					},
					{
						title: "Practical Skills",
						description: "Developed hands-on experience with professional security tools and techniques"
					},
					{
						title: "Methodological Approach",
						description: "Learned structured approaches to security testing and vulnerability assessment"
					},
					{
						title: "Career Advancement",
						description: "Enhanced qualifications for cybersecurity roles"
					}
				],
				responsibilities: [
					"Completing comprehensive course modules",
					"Performing hands-on security exercises",
					"Practicing with professional security tools",
					"Analyzing various attack vectors",
					"Conducting simulated penetration tests",
					"Documenting security findings",
					"Developing exploit code ethically",
					"Testing security configurations"
				],
				challenges: [
					{
						challenge: "Understanding complex attack vectors",
						solution: "Created detailed study notes and practiced with lab environments"
					},
					{
						challenge: "Mastering multiple security tools",
						solution: "Dedicated extra time to tool practice and documentation review"
					},
					{
						challenge: "Applying theoretical concepts practically",
						solution: "Conducted additional exercises and real-world scenario simulations"
					},
					{
						challenge: "Keeping up with course intensity",
						solution: "Developed a structured study schedule and review system"
					}
				],
				outcomes: [
					"Successfully completed course certification",
					"Mastered essential security tools and methodologies",
					"Developed practical penetration testing skills",
					"Created a personal security testing methodology",
					"Built a comprehensive toolkit for security assessment",
					"Improved understanding of system vulnerabilities",
					"Enhanced ability to write security reports"
				],
				careerConnections: [
					{
						title: "Professional Development",
						description: "The course directly supports my career goal of becoming a cybersecurity professional by providing comprehensive knowledge and hands-on experience in ethical hacking methodologies."
					},
					{
						title: "Technical Expertise",
						description: "The practical skills and tools mastered during the course form a strong foundation for conducting professional security assessments and penetration tests."
					},
					{
						title: "Career Readiness",
						description: "The certification and hands-on experience make me better prepared for cybersecurity roles and demonstrate my commitment to the field."
					}
				]
			},
			sushiShop: {
				title: "Desk Vendor at Sushi Shop",
				subtitle: "A fast-paced retail experience focusing on customer service and team coordination",
				overview: "From August 2022 to January 2023, I worked as a desk vendor at Sushi Shop, managing customer orders, coordinating with delivery personnel, and ensuring efficient service delivery while maintaining high standards of customer satisfaction.",
				skills: [
					"Customer Service Excellence",
					"Sales Techniques",
					"Team Coordination",
					"Order Management",
					"Time Management",
					"Problem-Solving",
					"Communication Skills",
					"Quality Control"
				],
				careerBenefits: [
					{
						title: "Team Dynamics",
						description: "Experience working in a fast-paced team environment with clear communication requirements"
					},
					{
						title: "Customer Relations",
						description: "Developed strong client interaction and problem-resolution skills"
					},
					{
						title: "Operations Management",
						description: "Gained practical experience in managing workflows and coordinating multiple tasks"
					},
					{
						title: "Leadership Skills",
						description: "Developed ability to take initiative and guide team activities during busy periods"
					}
				],
				responsibilities: [
					"Managing customer orders and inquiries",
					"Coordinating with delivery personnel",
					"Ensuring order accuracy and quality",
					"Handling cash transactions",
					"Maintaining clean and organized workspace",
					"Processing phone and in-person orders",
					"Resolving customer concerns",
					"Supporting team during peak hours"
				],
				challenges: [
					{
						challenge: "Managing high-volume periods efficiently",
						solution: "Developed systematic approach to order processing and team coordination"
					},
					{
						challenge: "Ensuring order accuracy under pressure",
						solution: "Implemented double-check system and clear communication protocols"
					},
					{
						challenge: "Balancing multiple tasks simultaneously",
						solution: "Created priority system for managing orders, deliveries, and customer service"
					},
					{
						challenge: "Handling customer complaints effectively",
						solution: "Developed structured approach to problem resolution and customer satisfaction"
					}
				],
				outcomes: [
					"Successfully managed high-volume customer service operations",
					"Improved order processing efficiency",
					"Enhanced team coordination and communication",
					"Developed strong customer relationship skills",
					"Contributed to positive customer experiences",
					"Gained valuable retail management experience"
				],
				careerConnections: [
					{
						title: "Professional Growth",
						description: "The fast-paced environment and need for clear communication have strengthened my ability to work effectively under pressure and maintain professional standards in challenging situations."
					},
					{
						title: "Team Collaboration",
						description: "Working in a coordinated team setting has enhanced my ability to collaborate effectively and contribute to team success, skills that are valuable in any professional environment."
					},
					{
						title: "Problem-Solving",
						description: "The daily challenges of retail service have improved my ability to think quickly and find effective solutions, skills that are directly applicable to technical problem-solving in IT."
					}
				]
			},
			axaHackathon: {
				title: "AXA IT Job Days & Hackathon",
				subtitle: "An intensive innovation challenge in a corporate environment",
				overview: "Participated in AXA's IT Job Days and Hackathon, working collaboratively on innovative solutions while gaining valuable exposure to a corporate tech environment and building professional connections with industry experts.",
				skills: [
					"Team Collaboration",
					"Problem-Solving",
					"Innovation",
					"Technical Communication",
					"Project Management",
					"Time Management",
					"Presentation Skills",
					"Corporate Environment Navigation"
				],
				careerBenefits: [
					{
						title: "Industry Network",
						description: "Built valuable connections with IT professionals and industry experts"
					},
					{
						title: "Corporate Experience",
						description: "Gained firsthand exposure to corporate tech environment and culture"
					},
					{
						title: "Innovation Practice",
						description: "Developed skills in rapid solution development and creative problem-solving"
					},
					{
						title: "Professional Growth",
						description: "Enhanced understanding of corporate IT practices and requirements"
					}
				],
				responsibilities: [
					"Participating in ideation sessions",
					"Collaborating with team members",
					"Developing innovative solutions",
					"Analyzing technical requirements",
					"Managing project timelines",
					"Presenting solutions to stakeholders",
					"Engaging with industry professionals",
					"Contributing to team discussions"
				],
				challenges: [
					{
						challenge: "Working under tight time constraints",
						solution: "Implemented effective time management and prioritization strategies"
					},
					{
						challenge: "Coordinating with diverse team members",
						solution: "Established clear communication channels and regular check-ins"
					},
					{
						challenge: "Meeting corporate requirements",
						solution: "Maintained focus on business value while ensuring technical feasibility"
					},
					{
						challenge: "Presenting complex solutions clearly",
						solution: "Developed concise, impact-focused presentation materials"
					}
				],
				outcomes: [
					"Successfully developed and presented innovative solutions",
					"Built meaningful professional connections",
					"Gained insight into corporate IT processes",
					"Enhanced team collaboration skills",
					"Improved understanding of business requirements",
					"Developed presentation and communication abilities"
				],
				careerConnections: [
					{
						title: "Professional Network",
						description: "The hackathon provided valuable opportunities to connect with industry professionals and understand corporate IT environments, supporting future career growth in technology."
					},
					{
						title: "Technical Skills",
						description: "Working on real-world challenges enhanced my ability to develop practical solutions while considering business requirements and constraints."
					},
					{
						title: "Career Development",
						description: "The experience provided insight into corporate IT culture and practices, helping to inform my career path and professional development goals."
					}
				]
			},
			// Add other project translations here...
		},
		learnMore: "Learn More",
		viewDetails: "View Details",
		// Contact
		totalHours: "Total hours",
		contactTitle: "Get In Touch",
		contactSubtitle: "Let's work together",
		nameLabel: "Name",
		emailLabel: "Email",
		messageLabel: "Message",
		sendMessage: "Send Message",
		namePlaceholder: "Enter your name",
		emailPlaceholder: "Enter your email",
		messagePlaceholder: "Enter your message",

		// Footer
		rightsReserved: "All rights reserved",
		madeWith: "Made with",
		and: "and",

		// Misc
		loading: "Loading...",
		error: "Something went wrong",
		success: "Message sent successfully!",

		careerPlanIntro1:
			"My career journey is focused on mastering cybersecurity while pursuing entrepreneurial opportunities. With 2-3 years of hands-on experience in CTF competitions and specialized training, I'm building a strong foundation in security testing and vulnerability assessment.",
		careerPlanIntro2:
			"In parallel with my cybersecurity pursuits, I'm co-founding a tech startup with Samuel Lambert, developing innovative mobile solutions for inventory management.",

		cyberSecurityTrainingDescription:
			"Building expertise through formal education and practical challenges to develop a comprehensive understanding of security concepts.",
		pentestCareerDescription:
			"Applying security knowledge to identify vulnerabilities and strengthen systems against potential threats.",
		entrepreneurshipDescription:
			"Co-developing a mobile application with Samuel Lambert to help businesses efficiently manage their inventory systems.",
	},
	fr: {
		 // General
		back: "Retour à la Formation",

		// Education Table Headers
		educationTableHeaders: {
			projectName: "Nom du Projet",
			realHours: "Heures Réelles",
			validatedHours: "Heures Validées",
			theme: "Thème",
			details: "Détails"
		},

		// Navbar
		home: "Accueil",
		about: "À propos",
		projects: "Projets",
		experience: "Expérience",
		education: "Formation",
		contact: "Contact",
		careerPlan: "Plan de Carrière",
		portfolio: "Mon Portfolio",

		// Hero section
		greeting: "Bonjour, je suis",
		role: "Développeur Full Stack",
		heroDesc:
			"Passionné par la création d'applications web responsives avec des technologies modernes. Je me concentre sur le développement de solutions propres, efficaces et conviviales.",
		downloadResume: "Télécharger CV",
		contactMe: "Me Contacter",

		// About section
		aboutTitle: "À propos de moi",
		aboutSubtitle: "Apprenez à mieux me connaître",
		aboutWhoAmI: "Qui je suis",
		aboutParagraph1:
			"Je suis un développeur Full Stack passionné avec un fort accent sur les technologies web et une expertise croissante en cybersécurité. Avec une expérience en développement front-end et back-end, je m'efforce de créer des applications conviviales et transparentes qui résolvent des problèmes réels.",
		aboutParagraph2:
			"Mes points forts incluent la résolution de problèmes, l'adaptation rapide aux nouvelles technologies et la communication efficace avec les parties prenantes techniques et non techniques. Je m'épanouis dans des environnements collaboratifs tout en étant suffisamment autodiscipliné pour travailler de manière indépendante.",
		aboutParagraph3:
			"Au-delà du codage, je poursuis activement des connaissances en cybersécurité par le biais de formations et de pratique dans les compétitions CTF, dans le but de combiner expertise en développement et en sécurité.",
		aboutSkills: "Compétences Techniques",

		// Projects section
		projectsTitle: "Projets",
		projectsSubtitle: "Découvrez mes travaux récents",
		viewProject: "Voir le Projet",
		viewCode: "Voir le Code",
		technologies: "Technologies",

		// Career Plan
		careerPlanTitle: "Plan de Carrière",
		careerPlanSubtitle: "Mon parcours en cybersécurité et entrepreneuriat",
		shortTerm: "Objectifs à Court Terme",
		shortTermGoals: [
			"Maîtriser les écosystèmes React et Node.js",
			"Contribuer à des projets open-source",
			"Améliorer mes compétences en design UI/UX",
		],
		midTerm: "Objectifs à Moyen Terme",
		midTermGoals: [
			"Diriger le développement de projets à moyenne et grande échelle",
			"Se spécialiser en architecture cloud",
			"Mentorer des développeurs juniors",
		],
		longTerm: "Objectifs à Long Terme",
		longTermGoals: [
			"Devenir architecte technique",
			"Démarrer une entreprise de conseil en technologie",
			"Intervenir lors de conférences technologiques internationales",
		],
		cyberSecurityTraining: "Formation en Cybersécurité",
		cyberSecurityTrainingGoals: [
			"Poursuite d'un master en cybersécurité",
			"Participation à des compétitions CTF depuis 2-3 ans",
			"Suivi de tutoriels et formations spécialisés",
			"Développement de compétences pratiques via des projets concrets",
		],
		pentestCareer: "Carrière en Test d'Intrusion",
		pentestCareerGoals: [
			"Devenir un testeur d'intrusion certifié",
			"Maîtriser les méthodologies de test de sécurité",
			"Spécialisation en évaluation des vulnérabilités",
			"Contribuer à la communauté de la sécurité",
		],
		entrepreneurship: "Projet Entrepreneurial",
		entrepreneurshipGoals: [
			"Cofondation d'une startup tech avec Samuel Lambert",
			"Développement d'une application mobile de gestion de stocks",
			"Construction d'un modèle d'affaires évolutif",
			"Mise sur le marché de solutions innovantes",
		],

		// Experience
		experienceTitle: "Expérience Professionnelle",
		experienceSubtitle: "Mon parcours professionnel",
		present: "Présent",
		roles: {
			role1: "Stagiaire chez Axa",
			role2: "CEO",
			role3: "Technicien de terrain",
			role4: "Vendeur & Caissier",
			role5: "Vendeur en boutique",
			role6: "Gestionnaire de dossier",
			role7: "Animateur débutant",
		},
		companies: {
			company1: "Axa Belgique",
			company2: "SRL SAMJU",
			company3: "Mistergenius",
			company4: "Spar",
			company5: "Sushi Shop",
			company6: "Axa Belgique",
			company7: "ASBL Ecole du Mouvement",
		},
		descriptions: {
			desc1: "Stagiaire chez Axa, travail sur différents projets pour l'entreprise",
			desc2: "Création de notre entreprise et développement de notre application mobile",
			desc3: "Aider les gens avec la technologie",
			desc4: "Vendeur et caissier dans un magasin de proximité",
			desc5: "Vendeur de sushi et dispatching des livraisons",
			desc6: "Travailler pour les clients d'axa et les aider avec leur dossier durant les inondations de 2021 en Belgique",
			desc7: "Constructeur de parcours pour les enfants et les animer tous les dimanche matin pendant 2 ans",
		},

		// Education
		educationTitle: "Formation",
		educationSubtitle: "Mon parcours académique",
		degrees: {
			degree1: "Master en Informatique",
			degree2: "Licence en Génie Logiciel",
		},
		schools: {
			school1: "Université Technique",
			school2: "École Supérieure",
		},
		eduDescriptions: {
			desc1: "Spécialisation en intelligence artificielle et technologies web",
			desc2: "Concentration sur le développement logiciel et la gestion de projet",
		},

			// Education Projects
		educationProjects: {
			technicalSupport: {
				title: "Support Technique chez MisterGenius",
				skills: "Job Etudiant",
				careerBenefit: "Développement du réseau, Compétences en communication interpersonnelle",
				description: "Travaillé comme technicien de terrain pour aider les gens à réparer leurs ordinateurs portables et répondre aux questions technologiques."
			},
			picoCTF: {
				title: "PicoCTF 2023",
				skills: "Capture The Flag",
				careerBenefit: "Amélioration des connaissances en sécurité et de la pensée analytique",
				description: "Participation au PicoCTF 2023, une compétition de cybersécurité axée sur la résolution de divers défis de sécurité et d'exercices de capture de drapeau."
			},
			stock4me: {
				title: "Stock4me - SAMJU Inc.",
				skills: "Entrepreneuriat",
				careerBenefit: "Développement commercial, Expérience startup, Gestion de projet",
				description: "Co-fondé SAMJU Inc. et développé Stock4me, une application mobile de gestion des stocks alimentée par l'IA."
			},
			cyberConference: {
				title: "Cybersécurité : Un Combat Virtuel Bien Réel",
				skills: "Conférence",
				careerBenefit: "Perspectives de l'industrie, Réseautage professionnel, Sensibilisation à la sécurité",
				description: "Participation à une conférence sur la cybersécurité présentée par Vincent Ceriani et Benoit Debande à l'Ephec Louvain-La-Neuve, axée sur les menaces cyber réelles et les stratégies de défense."
			},
			odooVisit: {
				title: "Visite des Bureaux Odoo",
				skills: "Visite bureaux",
				careerBenefit: "Exposition à l'industrie, Compréhension de l'environnement professionnel, Pratiques de développement logiciel",
				description: "Visite des bureaux d'Odoo à Louvain-La-Neuve, découverte de leur environnement de travail, pratiques de développement et participation à des exercices pratiques lors d'une session interactive."
			},
			ethicalHacking: {
				title: "Hacking Éthique : Le Cours Complet",
				skills: "E-Formation",
				careerBenefit: "Connaissances avancées en sécurité, Compétences pratiques en hacking, Certification professionnelle",
				description: "Suivi d'un cours complet de hacking éthique sur Udemy par Michel Kartner et Cyberini Formation, couvrant les tests d'intrusion, l'analyse de sécurité et les méthodologies de hacking éthique."
			},
			sushiShop: {
				title: "Vendeur en Boutique chez Sushi Shop",
				skills: "Soft Skills",
				careerBenefit: "Compétences interpersonnelles, Gestion d'équipe, Résolution de problèmes en environnement rapide",
				description: "Travaillé comme vendeur en boutique chez Sushi Shop d'août 2022 à janvier 2023, gérant les commandes clients, coordonnant avec le personnel de livraison et assurant un service efficace."
			},
			axaHackathon: {
				title: "AXA IT Job Days & Hackathon",
				skills: "Hackathon",
				careerBenefit: "Réseautage professionnel, Résolution de problèmes réels, Exposition à l'environnement corporate",
				description: "Participation aux AXA IT Job Days et Hackathon, travaillant sur des solutions innovantes dans un environnement corporate tout en développant un réseau professionnel."
			},
			spar: {
				title: "Vendeur & Caissier chez Spar Mont-Saint-Guibert",
				skills: "Service Client",
				careerBenefit: "Résolution de problèmes, Compétences interpersonnelles, Gestion du temps, Capacités de multitâche",
				description: "Travaillé comme vendeur et caissier chez Spar Mont-Saint-Guibert de septembre 2023 à décembre 2023, gérant les transactions clients, les stocks du magasin et fournissant un excellent service client."
				},
			
			axaStudentJob: {
				title: "AXA Belgium - Gestion des Dossiers Pendant les Inondations 2021",
				skills: "Job Etudiant",
				careerBenefit: "Amélioration des compétences en gestion de crise et optimisation des processus dans un environnement d'assurance pendant une catastrophe naturelle",
				description: "Gestion des dossiers clients pendant la crise des inondations en Belgique, mise en place d'améliorations d'efficacité et soutien aux opérations de l'équipe"
			},
		},

			// Project Pages
		projectPages: {
			proofOfParticipation: "Preuve de Participation",
			visitProject: "Visiter le Projet",
			visitWebsite: "Visiter le Site Web",
			viewDocumentation: "Voir la Documentation",
			viewGithub: "Voir le GitHub",
			backToEducation: "Retour à la Formation",
			projectOverview: "Aperçu du Projet",
			skillsDeveloped: "Compétences Développées",
			careerBenefits: "Avantages Professionnels",
			projectDetails: "Détails du Projet",
			responsibilities: "Responsabilités",
			challengesSolutions: "Défis & Solutions",
			challenge: "Défi",
			solution: "Solution",
			keyOutcomes: "Résultats Clés",
			careerPlanConnection: "Lien avec le Plan de Carrière",
			
			technicalSupport: {
				title: "Support Technique chez MisterGenius",
				subtitle: "Projet de 10 heures axé sur le développement des compétences techniques et de communication",
				overview: "En tant que technicien de terrain chez MisterGenius, j'ai fourni un support technique sur site aux clients confrontés à divers problèmes matériels et logiciels. Cette expérience pratique m'a permis de développer des compétences concrètes en dépannage tout en améliorant mes capacités de communication avec les clients.",
				skills: [
					"Diagnostic et réparation matérielle",
					"Dépannage logiciel",
					"Excellence du service client",
					"Communication technique",
					"Résolution de problèmes sous pression",
					"Gestion du temps et efficacité"
				],
				careerBenefits: [
					{
						title: "Réseau Professionnel",
						description: "Développement de relations avec les clients et les professionnels du secteur"
					},
					{
						title: "Compétences en Communication",
						description: "Amélioration de la capacité à expliquer des concepts techniques à des utilisateurs non techniques"
					},
					{
						title: "Adaptabilité",
						description: "Apprentissage du travail dans des environnements divers avec différents besoins clients"
					},
					{
						title: "Expertise Technique",
						description: "Acquisition d'une expérience pratique sur une large gamme de problèmes matériels et logiciels"
					}
				],
				responsibilities: [
					"Diagnostic et résolution de problèmes matériels sur divers modèles d'ordinateurs portables",
					"Dépannage de problèmes logiciels incluant les erreurs système, la suppression de malwares et les conflits d'applications",
					"Fourniture d'explications claires et de recommandations aux clients",
					"Documentation des interventions et des solutions mises en œuvre",
					"Suivi auprès des clients pour assurer leur satisfaction"
				],
				challenges: [
					{
						challenge: "Expliquer des problèmes techniques complexes à des clients non techniques",
						solution: "Développement d'analogies et d'explications simples pour rendre les concepts techniques accessibles"
					},
					{
						challenge: "Diagnostiquer des pannes matérielles intermittentes",
						solution: "Création d'une approche de test systématique pour identifier et isoler les composants problématiques"
					}
				],
				outcomes: [
					"Réception de retours positifs pour la communication claire et les solutions efficaces",
					"Construction d'un réseau de clients fidèles et de références",
					"Acquisition d'une expérience pratique en réparation d'ordinateurs portables et en dépannage"
				],
				careerConnections: [
					{
						title: "Base Technique",
						description: "L'expérience pratique en dépannage fournit une base solide pour comprendre les interactions matérielles et logicielles, ce qui est essentiel pour ma carrière en IT. Ces connaissances pratiques complètent mon apprentissage académique et me donnent une perspective réelle des systèmes technologiques."
					},
					{
						title: "Compétences en Communication",
						description: "La capacité à communiquer efficacement des concepts techniques à des utilisateurs non techniques est une compétence cruciale dans les rôles IT. Cette expérience a considérablement amélioré ma capacité à faire le pont entre l'expertise technique et la communication claire avec les clients, une compétence qui sera précieuse tout au long de ma carrière."
					},
					{
						title: "Réseau Professionnel",
						description: "La construction d'un réseau de clients satisfaits et de connexions dans l'industrie fournit des relations professionnelles précieuses qui peuvent mener à de futures opportunités. Ces connexions peuvent offrir du mentorat, des recommandations pour des emplois ou des projets collaboratifs au fil de ma progression professionnelle."
					}
				]
			},
			picoCTF: {
				title: "PicoCTF 2023 - Compétition de Cybersécurité",
				subtitle: "Une compétition capture-the-flag stimulante axée sur le développement de compétences pratiques en cybersécurité",
				overview: "Participation au PicoCTF 2023, une compétition de cybersécurité de premier plan conçue par des experts en sécurité de l'Université Carnegie Mellon. Cette compétition a fourni une expérience pratique dans divers aspects de la cybersécurité, de la cryptographie à l'exploitation binaire.",
				skills: [
					"Analyse cryptographique",
					"Exploitation binaire",
					"Évaluation des vulnérabilités web",
					"Rétro-ingénierie",
					"Investigation forensique",
					"Résolution de problèmes sous pression",
					"Maîtrise des outils de sécurité"
				],
				careerBenefits: [
					{
						title: "Expertise Technique",
						description: "Développement de compétences pratiques dans divers domaines de la cybersécurité"
					},
					{
						title: "Résolution de Problèmes",
						description: "Amélioration de la pensée analytique et des capacités de débogage"
					},
					{
						title: "Mentalité Sécurité",
						description: "Renforcement de la compréhension des vulnérabilités et des vecteurs d'attaque"
					},
					{
						title: "Reconnaissance Industrielle",
						description: "Acquisition d'expérience avec les outils et méthodologies de sécurité standard de l'industrie"
					}
				],
				responsibilities: [
					"Analyse et résolution de défis de sécurité complexes",
					"Mise en œuvre de diverses techniques cryptographiques",
					"Rétro-ingénierie de fichiers binaires",
					"Identification et exploitation de vulnérabilités web",
					"Analyse forensique de différents formats de données",
					"Documentation des solutions et méthodologies"
				],
				challenges: [
					{
						challenge: "Énigmes cryptographiques complexes nécessitant une compréhension approfondie",
						solution: "Développement d'une approche systématique pour analyser et casser diverses méthodes de chiffrement"
					},
					{
						challenge: "Défis sensibles au temps nécessitant une réflexion rapide",
						solution: "Création d'une méthodologie pour évaluer et prioriser rapidement les défis en fonction de leur complexité"
					},
					{
						challenge: "Problèmes avancés d'exploitation binaire",
						solution: "Construction d'une boîte à outils complète pour l'analyse et la manipulation de fichiers binaires"
					}
				],
				outcomes: [
					"Résolution réussie de multiples défis dans différents domaines de sécurité",
					"Développement d'une compréhension approfondie des concepts de sécurité variés",
					"Création d'une boîte à outils personnelle de scripts et méthodologies pour les défis futurs",
					"Amélioration de la capacité à penser comme un attaquant et un défenseur"
				],
				careerConnections: [
					{
						title: "Compétences Pratiques en Sécurité",
						description: "L'expérience pratique acquise grâce à PicoCTF s'aligne directement avec mon objectif de devenir un professionnel de la sécurité. Les défis reflètent des scénarios du monde réel, fournissant une expérience précieuse dans l'identification et l'exploitation des vulnérabilités."
					},
					{
						title: "Croissance Technique",
						description: "Chaque défi a nécessité l'apprentissage de nouveaux outils et techniques, contribuant à mon expertise technique en sécurité. Cette approche d'apprentissage continu est essentielle pour rester à jour dans le domaine de la cybersécurité."
					},
					{
						title: "Développement de la Résolution de Problèmes",
						description: "La diversité des défis de la compétition a renforcé mes capacités analytiques et de résolution de problèmes, compétences cruciales pour les rôles en cybersécurité et en développement logiciel."
					}
				]
			},
			stock4me: {
				title: "Stock4me - SAMJU Inc.",
				subtitle: "Co-fondation d'une startup et développement d'une solution de gestion des stocks alimentée par l'IA",
				overview: "Co-fondation de SAMJU Inc. et direction du développement de Stock4me, une application mobile innovante qui utilise l'intelligence artificielle pour optimiser les processus de gestion des stocks. Cette aventure entrepreneuriale combine expertise technique et sens des affaires.",
				skills: [
					"Développement d'applications mobiles",
					"Intégration IA/ML",
					"Développement commercial",
					"Gestion de projet",
					"Design UI/UX",
					"Architecture de base de données",
					"Leadership d'équipe"
				],
				careerBenefits: [
					{
						title: "Expérience Entrepreneuriale",
						description: "Acquisition d'une expérience directe dans la création et la gestion d'une startup technologique"
					},
					{
						title: "Leadership Technique",
						description: "Direction des décisions de développement et de la planification de l'architecture"
					},
					{
						title: "Sens des Affaires",
						description: "Développement de compétences en planification d'entreprise, analyse de marché et stratégie produit"
					},
					{
						title: "Gestion de Projet",
						description: "Gestion du calendrier de développement, des ressources et des attentes des parties prenantes"
					}
				],
				responsibilities: [
					"Direction de la vision produit et de la stratégie de développement",
					"Conception et mise en œuvre des fonctionnalités principales de l'application",
					"Intégration des capacités d'IA pour la prédiction des stocks",
					"Gestion de l'équipe de développement et du calendrier du projet",
					"Réalisation d'études de marché et d'analyses concurrentielles",
					"Création de plans d'affaires et de supports de présentation",
					"Engagement avec les clients et investisseurs potentiels"
				],
				challenges: [
					{
						challenge: "Équilibrer le développement technique avec les besoins commerciaux",
						solution: "Mise en place d'une méthodologie agile pour prioriser les fonctionnalités selon les retours du marché et la valeur commerciale"
					},
					{
						challenge: "Intégrer des capacités d'IA complexes dans une interface conviviale",
						solution: "Développement d'une architecture modulaire et de modèles d'interface utilisateur intuitifs pour simplifier l'interaction avec les fonctionnalités d'IA"
					},
					{
						challenge: "Gérer les contraintes de ressources dans un environnement de startup",
						solution: "Création de processus de développement efficaces et utilisation d'outils open-source pour maximiser la productivité"
					}
				],
				outcomes: [
					"Lancement réussi de la version bêta de l'application Stock4me",
					"Développement de fonctionnalités robustes de gestion des stocks alimentées par l'IA",
					"Construction d'une base solide pour la croissance et l'évolutivité de l'entreprise",
					"Établissement d'un réseau de clients et partenaires potentiels",
					"Création de processus efficaces de développement et d'affaires"
				],
				careerConnections: [
					{
						title: "Innovation Technologique",
						description: "Le développement de Stock4me a fourni une expérience inestimable dans la combinaison de technologies de pointe avec des solutions commerciales pratiques. Cela s'aligne parfaitement avec mon objectif de créer des solutions innovantes qui résolvent des problèmes du monde réel."
					},
					{
						title: "Développement du Leadership",
						description: "La gestion des aspects techniques et commerciaux de la startup a renforcé mes capacités de leadership, me préparant pour de futurs rôles en leadership technologique et entrepreneuriat."
					},
					{
						title: "Intégration Business & Technologie",
						description: "Cette expérience comble le fossé entre l'expertise technique et le sens des affaires, essentiel pour mon objectif à long terme de gérer des entreprises technologiques prospères."
					}
				]
			},
			cyberConference: {
				title: "Cybersécurité : Un Combat Virtuel Bien Réel",
				subtitle: "Une conférence enrichissante sur les défis et stratégies de défense en cybersécurité moderne",
				overview: "Participation à une conférence captivante sur la cybersécurité présentée par les experts de l'industrie Vincent Ceriani et Benoit Debande à l'Ephec Louvain-La-Neuve. La conférence a fourni des aperçus précieux sur les menaces cyber actuelles, les mécanismes de défense et l'évolution du paysage de la sécurité de l'information.",
				skills: [
					"Évaluation des menaces de sécurité",
					"Compréhension des stratégies de défense",
					"Concepts de sécurité réseau",
					"Analyse des risques",
					"Meilleures pratiques de sécurité",
					"Conscience des tendances de l'industrie",
					"Réseautage professionnel"
				],
				careerBenefits: [
					{
						title: "Perspectives de l'Industrie",
						description: "Acquisition de perspectives précieuses sur les défis et solutions de cybersécurité du monde réel"
					},
					{
						title: "Connexions Professionnelles",
						description: "Réseautage avec des experts de l'industrie et d'autres passionnés de cybersécurité"
					},
					{
						title: "Amélioration des Connaissances",
						description: "Approfondissement de la compréhension des menaces de sécurité actuelles et des stratégies de défense"
					},
					{
						title: "Orientation de Carrière",
						description: "Renforcement du parcours professionnel en cybersécurité grâce aux conseils d'experts"
					}
				],
				responsibilities: [
					"Participation active aux discussions de la conférence",
					"Prise de notes détaillées sur les concepts de sécurité présentés",
					"Engagement avec les intervenants lors des sessions de Q&R",
					"Analyse des études de cas et exemples présentés",
					"Réseautage avec des professionnels de l'industrie",
					"Réflexion sur les concepts appris pour une application pratique"
				],
				challenges: [
					{
						challenge: "Comprendre des concepts techniques complexes présentés rapidement",
						solution: "Développement d'un système de prise de notes efficace et révision des documents post-conférence"
					},
					{
						challenge: "Relier les concepts théoriques aux applications pratiques",
						solution: "Création d'exemples et de scénarios personnels pour contextualiser l'information"
					},
					{
						challenge: "Maximiser les opportunités de réseautage dans un temps limité",
						solution: "Préparation de questions pertinentes à l'avance et engagement actif pendant les pauses"
					}
				],
				outcomes: [
					"Meilleure compréhension du paysage actuel de la cybersécurité",
					"Établissement de connexions avec des professionnels de l'industrie",
					"Acquisition d'insights sur les défis de sécurité du monde réel",
					"Collection de ressources précieuses pour l'apprentissage continu",
					"Identification des domaines clés pour le développement des compétences"
				],
				careerConnections: [
					{
						title: "Développement Professionnel",
						description: "La conférence a fourni des aperçus cruciaux de l'industrie de la cybersécurité, aidant à façonner ma compréhension des exigences et opportunités de carrière. L'exposition aux défis et solutions du monde réel a informé mon parcours de développement professionnel."
					},
					{
						title: "Construction du Réseau",
						description: "Rencontrer et apprendre des professionnels expérimentés a élargi mon réseau professionnel et fourni de précieuses opportunités de mentorat. Ces connexions sont inestimables pour ma future croissance professionnelle en cybersécurité."
					},
					{
						title: "Application des Connaissances",
						description: "Les insights pratiques acquis lors de la conférence soutiennent directement mes objectifs en cybersécurité, fournissant un contexte réel à mon apprentissage technique et aidant à concentrer mes efforts de développement de compétences."
					}
				]
			},
			spar: {
				title: "Vendeur & Caissier chez Spar Mont-Saint-Guibert",
				subtitle: "Une expérience complète dans la vente au détail développant des compétences essentielles et une expertise opérationnelle",
				overview: "De septembre 2023 à décembre 2023, j'ai travaillé comme vendeur et caissier chez Spar Mont-Saint-Guibert, où j'ai développé des compétences essentielles en service client et acquis une précieuse expérience opérationnelle en gestion de commerce de détail.",
				skills: [
					"Excellence du service client",
					"Gestion de caisse",
					"Contrôle des stocks",
					"Collaboration d'équipe",
					"Résolution de problèmes",
					"Gestion du temps"
				],
				careerBenefits: [
					{
						title: "Multitâche",
						description: "Gestion de multiples responsabilités"
					},
					{
						title: "Communication",
						description: "Interaction claire avec les clients"
					},
					{
						title: "Résolution de problèmes",
						description: "Résolution rapide des problèmes"
					},
					{
						title: "Travail d'équipe",
						description: "Collaboration efficace"
					}
				],
				responsibilities: {
					customerService: {
						title: "Service Client",
						description: "Fournir un excellent service client et gérer les demandes de manière professionnelle"
					},
					cashHandling: {
						title: "Gestion de Caisse",
						description: "Gérer les transactions avec précision et maintenir l'équilibre de la caisse"
					},
					inventoryManagement: {
						title: "Gestion des Stocks",
						description: "Surveiller les niveaux de stock et assister au contrôle des inventaires"
					},
					storeOperations: {
						title: "Opérations du Magasin",
						description: "Maintenir la propreté du magasin et l'organisation des produits"
					}
				},
				achievements: [
					"Fourniture constante d'un excellent service client",
					"Maintien de registres précis de gestion de caisse",
					"Contribution à l'efficacité des opérations du magasin",
					"Développement d'une solide connaissance des produits",
					"Construction de relations positives avec les clients réguliers"
				],
				careerConnections: [
					{
						title: "Développement des Compétences Relationnelles",
						description: "L'environnement de la vente au détail a offert d'excellentes opportunités de développer des compétences relationnelles cruciales telles que la communication, la résolution de problèmes et le travail d'équipe. Ces compétences sont précieuses dans tout environnement professionnel, y compris les rôles technologiques."
					},
					{
						title: "Croissance Professionnelle",
						description: "Travailler dans un environnement de vente au détail dynamique a permis de développer des capacités d'adaptation et de multitâche. Ces compétences sont particulièrement précieuses pour gérer plusieurs projets et délais dans une carrière technique."
					}
				],
				backButton: "Retour à la Formation"
			},
			odooVisit: {
				title: "Visite des Bureaux Odoo",
				subtitle: "Une exploration enrichissante des pratiques professionnelles de développement logiciel",
				overview: "Participation à une visite complète des bureaux d'Odoo à Louvain-La-Neuve, acquérant des aperçus précieux de leur environnement de travail, pratiques de développement et cycle de vie du développement logiciel à travers des sessions interactives et des exercices pratiques.",
				skills: [
					"Pratiques de développement logiciel",
					"Compréhension des systèmes ERP",
					"Conscience de l'environnement professionnel",
					"Connaissance du flux de travail de développement",
					"Méthodes de collaboration d'équipe",
					"Meilleures pratiques de l'industrie",
					"Communication technique"
				],
				careerBenefits: [
					{
						title: "Exposition à l'Industrie",
						description: "Aperçu direct de l'environnement professionnel de développement logiciel"
					},
					{
						title: "Réseau Professionnel",
						description: "Connexion avec des professionnels et développeurs de l'industrie"
					},
					{
						title: "Connaissances Techniques",
						description: "Compréhension du développement logiciel au niveau entreprise"
					},
					{
						title: "Orientation de Carrière",
						description: "Perspective précieuse sur les parcours de développement professionnel"
					}
				],
				responsibilities: [
					"Participation à des sessions interactives sur les pratiques de développement",
					"Engagement avec les développeurs et membres de l'équipe Odoo",
					"Apprentissage de l'architecture des systèmes ERP",
					"Compréhension des processus de développement agile",
					"Exploration des outils et méthodologies modernes de développement",
					"Analyse de scénarios réels de développement logiciel"
				],
				challenges: [
					{
						challenge: "Comprendre l'architecture logicielle complexe d'entreprise",
						solution: "Engagement actif avec les développeurs et prise de notes détaillées pour révision ultérieure"
					},
					{
						challenge: "Absorber une grande quantité d'informations techniques",
						solution: "Création d'une documentation structurée et pose de questions de clarification"
					},
					{
						challenge: "Relier les connaissances théoriques aux applications pratiques",
						solution: "Participation à des exercices pratiques et des exemples du monde réel"
					}
				],
				outcomes: [
					"Acquisition d'une perspective pratique sur le développement logiciel professionnel",
					"Compréhension des pratiques de développement au niveau entreprise",
					"Apprentissage des outils et méthodologies modernes de développement",
					"Construction de connexions avec des professionnels de l'industrie",
					"Amélioration de la compréhension des opportunités de carrière en développement logiciel"
				],
				careerConnections: [
					{
						title: "Développement Professionnel",
						description: "La visite a fourni un aperçu précieux des pratiques professionnelles de développement logiciel et de la culture d'entreprise, aidant à orienter mon parcours de développement professionnel et mes aspirations."
					},
					{
						title: "Croissance Technique",
						description: "La compréhension du développement logiciel et de l'architecture au niveau entreprise a élargi ma perspective technique et mis en évidence les domaines d'apprentissage et de développement futurs."
					},
					{
						title: "Compréhension de l'Industrie",
						description: "L'exposition directe à un environnement professionnel de développement logiciel a aidé à combler le fossé entre l'apprentissage académique et la pratique industrielle, fournissant un contexte précieux pour mon développement de carrière."
					}
				]
			},
			ethicalHacking: {
				title: "Hacking Éthique : Le Cours Complet",
				subtitle: "Un voyage approfondi dans le hacking éthique et les fondamentaux de la cybersécurité",
				overview: "Suivi d'un cours complet de hacking éthique sur Udemy par Michel Kartner et Cyberini Formation, acquérant une connaissance approfondie des tests d'intrusion, de l'analyse de sécurité et des méthodologies de hacking éthique à travers la pratique et des scénarios réels.",
				skills: [
					"Méthodologies de Test d'Intrusion",
					"Analyse de Sécurité Réseau",
					"Évaluation des Vulnérabilités",
					"Maîtrise des Outils de Sécurité",
					"Techniques d'Ingénierie Sociale",
					"Sécurité des Applications Web",
					"Exploitation des Systèmes",
					"Réponse aux Incidents",
					"Rédaction de Rapports de Sécurité"
				],
				careerBenefits: [
					{
						title: "Certification Professionnelle",
						description: "Obtention d'une certification reconnue dans le hacking éthique"
					},
					{
						title: "Compétences Pratiques",
						description: "Développement d'une expérience pratique avec des outils et techniques de sécurité professionnels"
					},
					{
						title: "Approche Méthodologique",
						description: "Apprentissage d'approches structurées pour les tests de sécurité et l'évaluation des vulnérabilités"
					},
					{
						title: "Avancement de Carrière",
						description: "Amélioration des qualifications pour les rôles en cybersécurité"
					}
				],
				responsibilities: [
					"Complétion des modules de cours complets",
					"Réalisation d'exercices de sécurité pratiques",
					"Pratique avec des outils de sécurité professionnels",
					"Analyse de divers vecteurs d'attaque",
					"Conduite de tests d'intrusion simulés",
					"Documentation des résultats de sécurité",
					"Développement éthique de code d'exploitation",
					"Test des configurations de sécurité"
				],
				challenges: [
					{
						challenge: "Comprendre des vecteurs d'attaque complexes",
						solution: "Création de notes d'étude détaillées et pratique dans des environnements de laboratoire"
					},
					{
						challenge: "Maîtriser plusieurs outils de sécurité",
						solution: "Consacrer du temps supplémentaire à la pratique des outils et à la revue de la documentation"
					},
					{
						challenge: "Appliquer les concepts théoriques en pratique",
						solution: "Réalisation d'exercices supplémentaires et simulation de scénarios réels"
					},
					{
						challenge: "Suivre l'intensité du cours",
						solution: "Développement d'un programme d'étude structuré et d'un système de révision"
					}
				],
				outcomes: [
					"Obtention réussie de la certification du cours",
					"Maîtrise des outils et méthodologies de sécurité essentiels",
					"Développement de compétences pratiques en test d'intrusion",
					"Création d'une méthodologie personnelle de test de sécurité",
					"Construction d'une boîte à outils complète pour l'évaluation de la sécurité",
					"Amélioration de la compréhension des vulnérabilités système",
					"Renforcement de la capacité à rédiger des rapports de sécurité"
				],
				careerConnections: [
					{
						title: "Développement Professionnel",
						description: "Le cours soutient directement mon objectif de carrière de devenir un professionnel de la cybersécurité en fournissant des connaissances complètes et une expérience pratique des méthodologies de hacking éthique."
					},
					{
						title: "Expertise Technique",
						description: "Les compétences pratiques et les outils maîtrisés pendant le cours constituent une base solide pour la conduite d'évaluations de sécurité et de tests d'intrusion professionnels."
					},
					{
						title: "Préparation à la Carrière",
						description: "La certification et l'expérience pratique me rendent mieux préparé pour les rôles en cybersécurité et démontrent mon engagement dans le domaine."
					}
				]
			},
			sushiShop: {
				title: "Vendeur en Boutique chez Sushi Shop",
				subtitle: "Une expérience de vente au détail dynamique axée sur le service client et la coordination d'équipe",
				overview: "D'août 2022 à janvier 2023, j'ai travaillé comme vendeur en boutique chez Sushi Shop, gérant les commandes clients, coordonnant avec le personnel de livraison et assurant un service efficace tout en maintenant des standards élevés de satisfaction client.",
				skills: [
					"Excellence du Service Client",
					"Techniques de Vente",
					"Coordination d'Équipe",
					"Gestion des Commandes",
					"Gestion du Temps",
					"Résolution de Problèmes",
					"Compétences en Communication",
					"Contrôle Qualité"
				],
				careerBenefits: [
					{
						title: "Dynamique d'Équipe",
						description: "Expérience de travail dans un environnement d'équipe dynamique avec des exigences de communication claires"
					},
					{
						title: "Relations Client",
						description: "Développement de solides compétences en interaction client et résolution de problèmes"
					},
					{
						title: "Gestion des Opérations",
						description: "Acquisition d'une expérience pratique dans la gestion des flux de travail et la coordination de multiples tâches"
					},
					{
						title: "Compétences de Leadership",
						description: "Développement de la capacité à prendre des initiatives et à guider les activités de l'équipe pendant les périodes chargées"
					}
				],
				responsibilities: [
					"Gestion des commandes et demandes clients",
					"Coordination avec le personnel de livraison",
					"Garantie de l'exactitude et de la qualité des commandes",
					"Gestion des transactions en espèces",
					"Maintien d'un espace de travail propre et organisé",
					"Traitement des commandes par téléphone et en personne",
					"Résolution des problèmes clients",
					"Soutien à l'équipe pendant les heures de pointe"
				],
				challenges: [
					{
						challenge: "Gérer efficacement les périodes de forte affluence",
						solution: "Développement d'une approche systématique pour le traitement des commandes et la coordination d'équipe"
					},
					{
						challenge: "Assurer l'exactitude des commandes sous pression",
						solution: "Mise en place d'un système de double vérification et de protocoles de communication clairs"
					},
					{
						challenge: "Équilibrer plusieurs tâches simultanément",
						solution: "Création d'un système de priorité pour la gestion des commandes, des livraisons et du service client"
					},
					{
						challenge: "Gérer efficacement les plaintes des clients",
						solution: "Développement d'une approche structurée pour la résolution des problèmes et la satisfaction client"
					}
				],
				outcomes: [
					"Gestion réussie des opérations de service client à fort volume",
					"Amélioration de l'efficacité du traitement des commandes",
					"Renforcement de la coordination et de la communication d'équipe",
					"Développement de solides compétences en relations client",
					"Contribution à des expériences client positives",
					"Acquisition d'une expérience précieuse en gestion de commerce de détail"
				],
				careerConnections: [
					{
						title: "Croissance Professionnelle",
						description: "L'environnement dynamique et le besoin de communication claire ont renforcé ma capacité à travailler efficacement sous pression et à maintenir des standards professionnels dans des situations difficiles."
					},
					{
						title: "Collaboration d'Équipe",
						description: "Le travail dans un cadre d'équipe coordonné a amélioré ma capacité à collaborer efficacement et à contribuer au succès de l'équipe, des compétences précieuses dans tout environnement professionnel."
					},
					{
						title: "Résolution de Problèmes",
						description: "Les défis quotidiens du service de vente au détail ont amélioré ma capacité à penser rapidement et à trouver des solutions efficaces, des compétences directement applicables à la résolution de problèmes techniques en informatique."
					}
				]
			},
			axaHackathon: {
				title: "AXA IT Job Days & Hackathon",
				subtitle: "Un défi d'innovation intensif dans un environnement d'entreprise",
				overview: "Participation aux IT Job Days et Hackathon d'AXA, travaillant en collaboration sur des solutions innovantes tout en acquérant une exposition précieuse à un environnement technologique d'entreprise et en établissant des connexions professionnelles avec des experts de l'industrie.",
				skills: [
					"Collaboration d'Équipe",
					"Résolution de Problèmes",
					"Innovation",
					"Communication Technique",
					"Gestion de Projet",
					"Gestion du Temps",
					"Compétences de Présentation",
					"Navigation en Environnement Corporate"
				],
				careerBenefits: [
					{
						title: "Réseau Professionnel",
						description: "Construction de connexions précieuses avec des professionnels de l'IT et des experts de l'industrie"
					},
					{
						title: "Expérience Corporate",
						description: "Acquisition d'une exposition directe à l'environnement et à la culture tech d'entreprise"
					},
					{
						title: "Pratique de l'Innovation",
						description: "Développement de compétences en développement rapide de solutions et résolution créative de problèmes"
					},
					{
						title: "Croissance Professionnelle",
						description: "Amélioration de la compréhension des pratiques et exigences IT en entreprise"
					}
				],
				responsibilities: [
					"Participation aux sessions d'idéation",
					"Collaboration avec les membres de l'équipe",
					"Développement de solutions innovantes",
					"Analyse des exigences techniques",
					"Gestion des délais du projet",
					"Présentation des solutions aux parties prenantes",
					"Engagement avec des professionnels de l'industrie",
					"Contribution aux discussions d'équipe"
				],
				challenges: [
					{
						challenge: "Travailler sous des contraintes de temps serrées",
						solution: "Mise en place de stratégies efficaces de gestion du temps et de priorisation"
					},
					{
						challenge: "Coordonner avec des membres d'équipe divers",
						solution: "Établissement de canaux de communication clairs et de points réguliers"
					},
					{
						challenge: "Répondre aux exigences de l'entreprise",
						solution: "Maintien de l'accent sur la valeur commerciale tout en assurant la faisabilité technique"
					},
					{
						challenge: "Présenter clairement des solutions complexes",
						solution: "Développement de supports de présentation concis et axés sur l'impact"
					}
				],
				outcomes: [
					"Développement et présentation réussis de solutions innovantes",
					"Construction de connexions professionnelles significatives",
					"Acquisition d'une compréhension des processus IT d'entreprise",
					"Amélioration des compétences de collaboration en équipe",
					"Meilleure compréhension des exigences commerciales",
					"Développement des capacités de présentation et de communication"
				],
				careerConnections: [
					{
						title: "Réseau Professionnel",
						description: "Le hackathon a fourni des opportunités précieuses de connexion avec des professionnels de l'industrie et de compréhension des environnements IT d'entreprise, soutenant la croissance future de carrière en technologie."
					},
					{
						title: "Compétences Techniques",
						description: "Le travail sur des défis réels a amélioré ma capacité à développer des solutions pratiques tout en tenant compte des exigences et contraintes commerciales."
					},
					{
						title: "Développement de Carrière",
						description: "L'expérience a fourni un aperçu de la culture et des pratiques IT d'entreprise, aidant à orienter mon parcours professionnel et mes objectifs de développement."
					}
				]
			},
			// Add other project translations here...
		},
		viewDetails: "Voir les détails",
		learnMore: "Apprendre Plus",

		// Contact
		contactTitle: "Entrer en Contact",
		contactSubtitle: "Travaillons ensemble",
		nameLabel: "Nom",
		emailLabel: "Email",
		messageLabel: "Message",
		sendMessage: "Envoyer le Message",
		namePlaceholder: "Entrez votre nom",
		emailPlaceholder: "Entrez votre email",
		messagePlaceholder: "Entrez votre message",
		totalHours: "Total heures",
		// Footer
		rightsReserved: "Tous droits réservés",
		madeWith: "Fait avec",
		and: "et",

		// Misc
		loading: "Chargement...",
		error: "Une erreur est survenue",
		success: "Message envoyé avec succès !",
		careerPlanIntro1:
			"Mon parcours professionnel est axé sur la maîtrise de la cybersécurité tout en poursuivant des opportunités entrepreneuriales. Avec 2-3 ans d'expérience pratique dans les compétitions CTF et des formations spécialisées, je construis une base solide en tests de sécurité et évaluation de vulnérabilités.",
		careerPlanIntro2:
			"En parallèle de mes activités en cybersécurité, je cofonde une startup technologique avec Samuel Lambert, développant des solutions mobiles innovantes pour la gestion des stocks.",

		cyberSecurityTrainingDescription:
			"Développement d'expertise par l'éducation formelle et des défis pratiques pour acquérir une compréhension complète des concepts de sécurité.",
		pentestCareerDescription:
			"Application des connaissances en sécurité pour identifier les vulnérabilités et renforcer les systèmes contre les menaces potentielles.",
		entrepreneurshipDescription:
			"Codéveloppement d'une application mobile avec Samuel Lambert pour aider les entreprises à gérer efficacement leurs systèmes d'inventaire.",
	},
};

// Create the language provider component
export const LanguageProvider = ({ children }) => {
	// Use browser language as initial value or default to English
	const initialLanguage = navigator.language?.startsWith("fr") ? "fr" : "en";

	const [language, setLanguage] = useState(initialLanguage);

	// Toggle between languages
	const toggleLanguage = () => {
		setLanguage((prevLang) => (prevLang === "en" ? "fr" : "en"));
	};

	// Get current text based on the selected language
	const t = (key) => {
		const keys = key.split(".");
		let value = translations[language];

		for (const k of keys) {
			if (value && value[k] !== undefined) {
				value = value[k];
			} else {
				console.warn(
					`Translation key "${key}" not found in language: ${language}`
				);
				return key;
			}
		}

		return value;
	};

	return (
		<LanguageContext.Provider value={{ language, toggleLanguage, t }}>
			{children}
		</LanguageContext.Provider>
	);
};

// Custom hook for using the language context
export const useLanguage = () => {
	const context = useContext(LanguageContext);
	if (context === undefined) {
		throw new Error("useLanguage must be used within a LanguageProvider");
	}
	return context;
};
